import { useState } from 'react';
import { X, CheckCircle, Flag, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TodoBulkActionsProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
  onExitSelectionMode?: () => void;
}

export function TodoBulkActions({ 
  selectedIds, 
  onClearSelection,
  onActionComplete,
  onExitSelectionMode
}: TodoBulkActionsProps) {
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  if (selectedIds.length === 0) return null;

  const handleMarkComplete = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unified_todos' as any)
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .in('id', selectedIds);

      if (error) throw error;

      toast({
        title: 'Succes',
        description: `${selectedIds.length} taken gemarkeerd als voltooid`,
      });
      onClearSelection();
      onActionComplete();
    } catch (error) {
      console.error('Error completing todos:', error);
      toast({
        title: 'Fout',
        description: 'Fout bij voltooien van taken',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePriority = async (priority: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unified_todos' as any)
        .update({ priority })
        .in('id', selectedIds);

      if (error) throw error;

      toast({
        title: 'Succes',
        description: `Prioriteit gewijzigd voor ${selectedIds.length} taken`,
      });
      onClearSelection();
      onActionComplete();
    } catch (error) {
      console.error('Error updating priority:', error);
      toast({
        title: 'Fout',
        description: 'Fout bij wijzigen prioriteit',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unified_todos' as any)
        .update({ deleted_at: new Date().toISOString() })
        .in('id', selectedIds);

      if (error) throw error;

      toast({
        title: 'Succes',
        description: `${selectedIds.length} taken verwijderd`,
      });
      setShowDeleteDialog(false);
      onClearSelection();
      onActionComplete();
    } catch (error) {
      console.error('Error deleting todos:', error);
      toast({
        title: 'Fout',
        description: 'Fout bij verwijderen van taken',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
        <div className="flex items-center gap-3 bg-background border rounded-lg shadow-lg p-3">
          <Badge variant="secondary" className="gap-1">
            {selectedIds.length} geselecteerd
          </Badge>

          <div className="h-6 w-px bg-border" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkComplete}
            disabled={loading}
            className="gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Voltooien</span>
          </Button>

          <Select onValueChange={handleChangePriority} disabled={loading}>
            <SelectTrigger className="w-auto gap-2">
              <Flag className="h-4 w-4" />
              <span className="hidden sm:inline">Prioriteit</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Laag</SelectItem>
              <SelectItem value="medium">Middel</SelectItem>
              <SelectItem value="high">Hoog</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={loading}
            className="gap-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Verwijderen</span>
          </Button>

          <div className="h-6 w-px bg-border" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClearSelection();
              onExitSelectionMode?.();
            }}
            disabled={loading}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">Annuleren</span>
          </Button>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Taken verwijderen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {selectedIds.length} taken wilt verwijderen? 
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? 'Verwijderen...' : 'Verwijderen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
